import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

// Contexte pour les onglets
const TabGroupContext = createContext<{
  selectedIndex: number;
  onChange: (index: number) => void;
} | undefined>(undefined);

interface TabGroupProps {
  children: ReactNode;
  selectedIndex: number;
  onChange: (index: number) => void;
  className?: string;
}

interface TabProps {
  children: ReactNode;
  className?: string;
  disabled?: boolean;
}

interface TabListProps {
  children: ReactNode;
  className?: string;
}

interface TabPanelProps {
  children: ReactNode;
  className?: string;
}

export function TabGroup({ children, selectedIndex, onChange, className }: TabGroupProps) {
  return (
    <TabGroupContext.Provider value={{ selectedIndex, onChange }}>
      <div className={className}>{children}</div>
    </TabGroupContext.Provider>
  );
}

export function TabList({ children, className }: TabListProps) {
  return (
    <div role="tablist" className={className}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            index,
          });
        }
        return child;
      })}
    </div>
  );
}

export function Tab({ children, className, disabled, ...rest }: TabProps & { index?: number }) {
  const context = useContext(TabGroupContext);
  
  if (!context) {
    throw new Error('Tab must be used within a TabGroup');
  }
  
  const { selectedIndex, onChange } = context;
  const index = rest.index ?? 0;
  const isSelected = selectedIndex === index;
  
  return (
    <button
      role="tab"
      type="button"
      aria-selected={isSelected}
      disabled={disabled}
      data-selected={isSelected ? '' : undefined}
      className={className}
      onClick={() => !disabled && onChange(index)}
    >
      {children}
    </button>
  );
}

export function TabPanel({ children, className, index = 0 }: TabPanelProps & { index?: number }) {
  const context = useContext(TabGroupContext);
  
  if (!context) {
    throw new Error('TabPanel must be used within a TabGroup');
  }
  
  const { selectedIndex } = context;
  const isSelected = selectedIndex === index;
  
  if (!isSelected) {
    return null;
  }
  
  return (
    <div
      role="tabpanel"
      className={className}
    >
      {children}
    </div>
  );
}